import { userManagementApi } from './userManagementService';

// Tipuri pentru notificari
export interface Notification {
  id: string;
  userId?: string; // Deprecated - foloseste recipient_id
  recipient_id?: string;
  sender_id?: string;
  type: 'appointment_reminder' | 'vaccination_due' | 'treatment_reminder' | 'payment_confirmation' | 'emergency_alert' | 'general' | string;
  title: string;
  message: string;
  channels?: ('push' | 'email' | 'sms')[];
  data?: Record<string, any>;
  read: boolean;
  sent?: boolean;
  priority?: string;
  status?: string;
  scheduledAt?: string;
  sentAt?: string;
  createdAt?: string; // Deprecated - foloseste created_at
  created_at?: string;
  updated_at?: string;
  read_at?: string;
  senderFirstName?: string;
  senderLastName?: string;
}

// Tipuri pentru setari notificari
export interface NotificationSettings {
  id: string;
  userId: string;
  clinicId: string;
  channels: {
    email: {
      enabled: boolean;
      types: string[];
      timeWindows: {
        start: string;
        end: string;
      }[];
    };
    sms: {
      enabled: boolean;
      types: string[];
      timeWindows: {
        start: string;
        end: string;
      }[];
    };
    push: {
      enabled: boolean;
      types: string[];
      timeWindows: {
        start: string;
        end: string;
      }[];
    };
    inApp: {
      enabled: boolean;
      types: string[];
      sound: boolean;
      desktop: boolean;
    };
  };
  types: {
    [key: string]: {
      enabled: boolean;
      channels: string[];
      frequency: 'immediate' | 'daily' | 'weekly' | 'never';
    };
  };
  rateLimiting: {
    enabled: boolean;
    maxPerHour: number;
    maxPerDay: number;
  };
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
    days: string[];
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru template-uri notificari
export interface NotificationTemplate {
  id: string;
  name: string;
  type: string;
  channel: 'email' | 'sms' | 'push' | 'inApp';
  subject?: string;
  content: string;
  variables: string[];
  isActive: boolean;
  clinicId: string;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru reguli automate
export interface NotificationRule {
  id: string;
  name: string;
  description: string;
  trigger: {
    event: string;
    conditions: {
      field: string;
      operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'not_contains';
      value: any;
    }[];
  };
  actions: {
    type: 'send_notification' | 'send_email' | 'send_sms' | 'create_task' | 'update_status';
    config: any;
  }[];
  isActive: boolean;
  clinicId: string;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru statistici notificari
export interface NotificationStats {
  total: number;
  unread: number;
  byType: {
    [key: string]: number;
  };
  byPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  byChannel: {
    email: number;
    sms: number;
    push: number;
    inApp: number;
  };
  deliveryRate: {
    email: number;
    sms: number;
    push: number;
    inApp: number;
  };
  readRate: number;
  responseRate: number;
}

// Tipuri pentru filtre
export interface NotificationFilters {
  type?: string;
  priority?: string;
  isRead?: boolean;
  isArchived?: boolean;
  startDate?: string;
  endDate?: string;
  userId?: string;
  clinicId?: string;
  page?: number;
  limit?: number;
}

class NotificationsService {
  private baseUrl = '/notifications';

  // Notificari
  async getNotifications(filters?: NotificationFilters): Promise<{ notifications: Notification[]; total: number; page: number; limit: number; }> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      // Backend contract supports: status, unread, page, limit, type
      if (filters?.isRead !== undefined) params.append('unread', (!filters.isRead).toString());
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await userManagementApi.get(`${this.baseUrl}?${params.toString()}`);
      const d = response.data ?? {};
      return {
        notifications: d.notifications ?? d.data?.notifications ?? [],
        total: d.total ?? d.data?.total ?? 0,
        page: d.page ?? d.data?.page ?? (filters?.page || 1),
        limit: d.limit ?? d.data?.limit ?? (filters?.limit || 20),
      };
    } catch (error: any) {
      // Nu logăm eroarea dacă backend-ul nu rulează
      if (!(error?.message?.includes('Failed to fetch') || error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK')) {
        console.error('Error fetching notifications:', error);
      }
      throw error;
    }
  }

  async getNotificationById(id: string): Promise<Notification> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/${id}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error('Error fetching notification:', error);
      throw error;
    }
  }

  async markAsRead(id: string): Promise<void> {
    try {
      await userManagementApi.patch(`${this.baseUrl}/${id}/read`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markAsUnread(_id: string): Promise<void> {
    // Endpoint-ul /:id/unread nu exista in backend-ul oficial
    return Promise.reject(new Error('Not supported by backend: markAsUnread'));
  }

  /** Statistici notificări pentru utilizatorul curent (total, unread, etc.) – GET /notifications/stats */
  async getStats(): Promise<{ total?: number; unread?: number; [k: string]: unknown }> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/stats`);
      return response.data?.data ?? response.data ?? {};
    } catch (error: any) {
      if (!(error?.message?.includes('Failed to fetch') || error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK')) {
        console.error('Error fetching notification stats:', error);
      }
      return {};
    }
  }

  async markAllAsRead(): Promise<void> {
    try {
      await userManagementApi.patch(`${this.baseUrl}/read-all`);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  async archive(_id: string): Promise<void> {
    return Promise.reject(new Error('Not supported by backend: archive'));
  }

  async unarchive(_id: string): Promise<void> {
    return Promise.reject(new Error('Not supported by backend: unarchive'));
  }

  async delete(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  async deleteAll(userId: string, filters?: { type?: string; isArchived?: boolean }): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/delete-all`, {
        data: { userId, ...filters }
      });
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      throw error;
    }
  }

  // Setari notificari
  async getNotificationSettings(userId: string): Promise<NotificationSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/settings/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      throw error;
    }
  }

  async updateNotificationSettings(userId: string, settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/settings/${userId}`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }

  // Template-uri notificari
  async getNotificationTemplates(clinicId: string): Promise<{ templates: NotificationTemplate[]; total: number; }> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/templates?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification templates:', error);
      throw error;
    }
  }

  async getNotificationTemplateById(id: string): Promise<NotificationTemplate> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/templates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification template:', error);
      throw error;
    }
  }

  async createNotificationTemplate(template: Omit<NotificationTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<NotificationTemplate> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/templates`, template);
      return response.data;
    } catch (error) {
      console.error('Error creating notification template:', error);
      throw error;
    }
  }

  async updateNotificationTemplate(id: string, template: Partial<NotificationTemplate>): Promise<NotificationTemplate> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/templates/${id}`, template);
      return response.data;
    } catch (error) {
      console.error('Error updating notification template:', error);
      throw error;
    }
  }

  async deleteNotificationTemplate(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/templates/${id}`);
    } catch (error) {
      console.error('Error deleting notification template:', error);
      throw error;
    }
  }

  // Reguli automate
  async getNotificationRules(clinicId: string): Promise<{ rules: NotificationRule[]; total: number; }> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/rules?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification rules:', error);
      throw error;
    }
  }

  async getNotificationRuleById(id: string): Promise<NotificationRule> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/rules/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification rule:', error);
      throw error;
    }
  }

  async createNotificationRule(rule: Omit<NotificationRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<NotificationRule> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/rules`, rule);
      return response.data;
    } catch (error) {
      console.error('Error creating notification rule:', error);
      throw error;
    }
  }

  async updateNotificationRule(id: string, rule: Partial<NotificationRule>): Promise<NotificationRule> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/rules/${id}`, rule);
      return response.data;
    } catch (error) {
      console.error('Error updating notification rule:', error);
      throw error;
    }
  }

  async deleteNotificationRule(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/rules/${id}`);
    } catch (error) {
      console.error('Error deleting notification rule:', error);
      throw error;
    }
  }

  async testNotificationRule(id: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/rules/${id}/test`);
      return response.data;
    } catch (error) {
      console.error('Error testing notification rule:', error);
      throw error;
    }
  }

  // Statistici notificari
  async getNotificationStats(userId: string, filters?: { startDate?: string; endDate?: string; }): Promise<NotificationStats> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await userManagementApi.get(`${this.baseUrl}/stats/${userId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }

  // Actiuni notificari
  async executeNotificationAction(notificationId: string, actionId: string, data?: any): Promise<void> {
    try {
      await userManagementApi.post(`${this.baseUrl}/${notificationId}/actions/${actionId}`, data);
    } catch (error) {
      console.error('Error executing notification action:', error);
      throw error;
    }
  }

  // Testare notificari
  async sendTestNotification(userId: string, type: string, channel: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/test`, {
        userId,
        type,
        channel
      });
      return response.data;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }

  // Export notificari
  async exportNotifications(filters?: NotificationFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.isRead !== undefined) params.append('isRead', filters.isRead.toString());
      if (filters?.isArchived !== undefined) params.append('isArchived', filters.isArchived.toString());
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.clinicId) params.append('clinicId', filters.clinicId);

      const response = await userManagementApi.get(`${this.baseUrl}/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting notifications:', error);
      throw error;
    }
  }

  async getTypesList(): Promise<Array<{ value: string; label: string }>> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/types/list`);
      return response.data?.data ?? [];
    } catch (error) {
      console.error('Error fetching notification types list:', error);
      return [];
    }
  }
}

export const notificationsService = new NotificationsService();
